import React, { useCallback, useEffect, useRef } from 'react';
import { IconButton } from '@fluentui/react';
import { logFlowEvent } from 'app/modules/user/createTicket/flowTelemetry';
import {
    VIEW_TELEMETRY_SECTION,
    ViewTelemetryConstants
} from 'app/modules/newTicketView/telemetryConstants';
import './AddCustomerSuccessToast.scss';

interface IProps {
    customerName: string;
    onDismiss: () => void;
}

const AUTO_DISMISS_MS = 5000;

const AddCustomerSuccessToast: React.FC<IProps> = ({ customerName, onDismiss }) => {
    const dismissedRef = useRef(false);

    const dismiss = useCallback(
        (dismissedVia: 'auto' | 'close') => {
            if (dismissedRef.current) {
                return;
            }
            dismissedRef.current = true;
            logFlowEvent(ViewTelemetryConstants.ADD_CUSTOMER_SUCCEEDED_TOAST, VIEW_TELEMETRY_SECTION, {
                toastType: 'added',
                dismissedVia
            });
            onDismiss();
        },
        [onDismiss]
    );

    useEffect(() => {
        logFlowEvent(ViewTelemetryConstants.ADD_CUSTOMER_SUCCEEDED_TOAST, VIEW_TELEMETRY_SECTION, {
            toastType: 'added',
            dismissedVia: 'viewed'
        });
        const timer = window.setTimeout(() => dismiss('auto'), AUTO_DISMISS_MS);
        return () => window.clearTimeout(timer);
    }, [dismiss]);

    return (
        <div
            className="add-customer-success-toast"
            data-testid="add-customer-success-toast"
            role="status"
            aria-live="polite">
            <div className="toast-content">
                <span className="toast-title">Customer added</span>
                <span className="toast-body">{customerName} has been added to this ticket.</span>
            </div>
            <IconButton
                className="toast-dismiss"
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel="Dismiss customer added notification"
                data-testid="add-customer-toast-dismiss-button"
                onClick={() => dismiss('close')}
            />
        </div>
    );
};

export default AddCustomerSuccessToast;
